import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/button.component';

import { ExtendedReportTemplateList } from '@futrli/views/processors/reports/reportTemplatesList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { LISTING_PAGE_TABS_HEIGHT } from '@futrli/ui-components/components/listing-page/listing-page-tabs/listing-page-tabs';

import { VirtualizedCardList } from '../../../../ui/virtualized-card-list/virtualized-card-list';
import { VirtualizedCardListEmptyState } from '../../../../ui/virtualized-card-list/virtualized-card-list-empty-state';
import { TabErrorMessage } from '../error-message/tab-error-message';
import { useNavHeight } from '../../../../hooks/useNavHeight';
import { useStatusOrientationFilter } from '../shared/use-status-orientation-filter';
import { FUTRLI_TEMPLATES_STORAGE_KEY } from '../shared/sort-config';
import { ReportTemplatesFilters } from '../report-templates/report-templates-filters';
import { ReportTemplateRow, ROW_HEIGHT } from '../report-templates/report-template-row';
import { TemplateDialogState, TemplateDialogType } from '../report-templates/report-templates-tab';
import { DeleteTemplateDialogContainer } from '../../delete-template-dialog/delete-template-dialog-container';
import { PublishTemplateDialogContainer } from '../../publish-template-dialog/publish-template-dialog-container';
import { useFutrliTemplates } from './use-futrli-templates';

const statusAccessor = (item: ExtendedReportTemplateList) => item.status;
const nameAccessor = (item: ExtendedReportTemplateList) => item.name;
const orientationAccessor = (item: ExtendedReportTemplateList) => item.orientation;

export const FutrliTemplatesTab = () => {
  const intl = useIntl();
  const { organisationId = '' } = useParams();
  const { navHeight } = useNavHeight();
  const { futrliTemplates, isLoading, errorLoading } = useFutrliTemplates();
  const { futrliTemplate } = useSidebarContext();
  const [activeDialog, setActiveDialog] = useState<TemplateDialogState>({ type: null, item: null });

  const openDialog = useCallback((type: TemplateDialogType, item: ExtendedReportTemplateList) => {
    setActiveDialog({ type, item });
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog({ type: null, item: null });
  }, []);

  const {
    filteredData,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    sortValue,
    sortOptions,
    handleSortChange
  } = useStatusOrientationFilter<ExtendedReportTemplateList>({
    data: futrliTemplates,
    statusAccessor,
    publishedValue: 'published',
    nameAccessor,
    orientationAccessor,
    storageKey: FUTRLI_TEMPLATES_STORAGE_KEY,
    organisationId
  });

  const renderRow = useCallback(
    (item: ExtendedReportTemplateList) => (
      <ReportTemplateRow
        template={item}
        onDelete={() => openDialog('delete', item)}
        onPublish={() => openDialog('publish', item)}
        isFutrliTemplate
      />
    ),
    [openDialog]
  );

  return (
    <Box>
      <ListingPageHeader
        variant="tab-header"
        title={<FormattedMessage id="predict.futrli_templates" defaultMessage="Futrli templates" />}
      />
      {errorLoading ? (
        <TabErrorMessage />
      ) : (
        <>
          <ReportTemplatesFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            typeFilter={typeFilter}
            onTypeChange={setTypeFilter}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
            stickyTop={navHeight + LISTING_PAGE_TABS_HEIGHT}
            isFutrliTemplates
          />
          <Box mt="24px">
            <VirtualizedCardList<ExtendedReportTemplateList>
              data={filteredData}
              renderRow={renderRow}
              rowHeight={ROW_HEIGHT}
              isLoading={isLoading}
              emptyState={
                <VirtualizedCardListEmptyState
                  title={intl.formatMessage({
                    id: 'reporting.no_futrli_templates_to_show',
                    defaultMessage: 'No Futrli templates to show'
                  })}
                  description={intl.formatMessage({
                    id: 'reporting.no_futrli_templates_found_description',
                    defaultMessage: 'Get started with your first Futrli template'
                  })}
                  action={
                    <Button type="secondary" onClick={() => futrliTemplate.open()}>
                      <FormattedMessage id="reporting.create_futrli_template" defaultMessage="Create Futrli template" />
                    </Button>
                  }
                />
              }
            />
          </Box>
        </>
      )}
      {activeDialog.type === 'delete' && activeDialog.item && (
        <DeleteTemplateDialogContainer isOpen onClose={closeDialog} template={activeDialog.item} />
      )}
      {activeDialog.type === 'publish' && activeDialog.item && (
        <PublishTemplateDialogContainer isOpen onClose={closeDialog} template={activeDialog.item} isFutrliTemplate />
      )}
    </Box>
  );
};
